import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatNumber } from '../../i18n/i18n';

const STAT_COLORS: Record<string, string> = {
  coding: 'var(--color-stat-coding)', art: 'var(--color-stat-art)',
  sound: 'var(--color-stat-sound)', design: 'var(--color-stat-design)',
};

/**
 * Project detail dialog — full view of an in-progress project.
 * Shows stats, quality, bugs, team, and actions (ship, cancel, change marketing, reassign team).
 */
export function openProjectDetailDialog(
  parent: HTMLElement,
  store: GameStore,
  projectId: string,
  onClose: () => void,
): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: 'Project Details',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:8px;min-width:360px;max-height:500px;overflow-y:auto;';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:11px;min-height:14px;text-align:center;';

  function render() {
    container.innerHTML = '';
    const state = store.getState();
    const theme = store.getThemeData();
    const project = state.projects.find((p) => p.id === projectId);
    if (!project) {
      const msg = document.createElement('div');
      msg.textContent = 'Project no longer exists.';
      msg.style.cssText = 'color:var(--color-text-muted);text-align:center;padding:16px;';
      container.appendChild(msg);
      return;
    }

    // Header: name + phase
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
    const nameEl = document.createElement('strong');
    nameEl.textContent = project.name;
    nameEl.style.fontSize = '16px';
    const phaseEl = document.createElement('span');
    const phaseLabel = project.phase.charAt(0).toUpperCase() + project.phase.slice(1);
    phaseEl.textContent = `${phaseLabel} · ${Math.round(project.phaseProgress)}%`;
    phaseEl.style.cssText = 'font-size:11px;color:var(--color-text-muted);';
    header.appendChild(nameEl);
    header.appendChild(phaseEl);
    container.appendChild(header);

    // Info row
    const catDef = theme.entities.projectCategories.find((c) => c.id === project.category);
    const subDef = theme.entities.projectSubcategories.find((s) => s.id === project.subcategory);
    const info = document.createElement('div');
    info.style.cssText = 'font-size:11px;color:var(--color-text-secondary);';
    info.textContent = `${catDef?.name ?? project.category} · ${subDef?.name ?? project.subcategory} · ${project.platform} · ${project.size}`;
    container.appendChild(info);

    // Phase progress bar
    const phaseBar = document.createElement('div');
    phaseBar.className = 'progress-bar';
    phaseBar.style.height = '8px';
    const phaseFill = document.createElement('div');
    phaseFill.className = 'progress-bar__fill';
    phaseFill.style.cssText = `width:${Math.round(project.phaseProgress)}%;background:var(--color-info);`;
    phaseBar.appendChild(phaseFill);
    container.appendChild(phaseBar);

    // Stat bars
    const statsLabel = document.createElement('div');
    statsLabel.textContent = 'DEVELOPMENT STATS';
    statsLabel.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-top:4px;';
    container.appendChild(statsLabel);

    const maxStat = Math.max(...Object.values(project.progress), 1);
    for (const [stat, value] of Object.entries(project.progress)) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:6px;';

      const lbl = document.createElement('span');
      lbl.textContent = stat;
      lbl.style.cssText = `font-size:11px;width:50px;color:${STAT_COLORS[stat] ?? 'var(--color-text-muted)'};text-transform:capitalize;`;

      const bar = document.createElement('div');
      bar.className = 'progress-bar';
      bar.style.cssText = 'flex:1;height:10px;';
      const fill = document.createElement('div');
      fill.className = 'progress-bar__fill';
      fill.style.cssText = `width:${Math.min(100, (value / maxStat) * 100)}%;background:${STAT_COLORS[stat] ?? '#888'};`;
      bar.appendChild(fill);

      const val = document.createElement('span');
      val.textContent = formatNumber(Math.round(value));
      val.style.cssText = `font-size:11px;width:40px;text-align:right;font-family:var(--font-pixel);color:${STAT_COLORS[stat] ?? '#888'};`;

      row.appendChild(lbl);
      row.appendChild(bar);
      row.appendChild(val);
      container.appendChild(row);
    }

    // Quality + Bugs
    const qualBugs = document.createElement('div');
    qualBugs.style.cssText = 'display:flex;justify-content:space-around;text-align:center;margin-top:4px;padding:8px;background:var(--color-bg-primary);border-radius:4px;';
    const qualColor = project.quality >= 70 ? 'var(--color-success)' : project.quality >= 40 ? 'var(--color-warning)' : 'var(--color-danger)';
    qualBugs.innerHTML = `
      <div><div style="font-size:10px;color:var(--color-text-muted);">Quality</div><div style="font-size:20px;font-weight:700;font-family:var(--font-pixel);color:${qualColor};">${Math.round(project.quality)}%</div></div>
      <div><div style="font-size:10px;color:var(--color-text-muted);">Bugs</div><div style="font-size:20px;font-weight:700;font-family:var(--font-pixel);color:${project.bugs > 5 ? 'var(--color-danger)' : 'var(--color-text-primary)'};">${Math.round(project.bugs)}</div></div>
    `;
    container.appendChild(qualBugs);

    // Team section
    const team = state.teams.find((t) => t.assignedProjectId === project.id);
    if (team) {
      const teamLabel = document.createElement('div');
      teamLabel.textContent = `TEAM: ${team.name}`;
      teamLabel.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-top:4px;';
      container.appendChild(teamLabel);

      const members = team.memberIds
        .map((id) => state.staff.find((s) => s.id === id))
        .filter((s): s is NonNullable<typeof s> => !!s);

      for (const m of members) {
        const mRow = document.createElement('div');
        mRow.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;padding:2px 0;';
        const roleDef = theme.entities.staffRoles.find((r) => r.id === m.role) ?? theme.entities.founderRole;
        mRow.innerHTML = `<span>${m.name} <span style="color:var(--color-text-muted);">(${roleDef.name} Lv.${m.level})</span></span>`;

        const statusTags: string[] = [];
        if (m.isTraining) statusTags.push('Training');
        if (m.isOnLeave) statusTags.push('Leave');
        if (statusTags.length > 0) {
          const tagEl = document.createElement('span');
          tagEl.textContent = statusTags.join(', ');
          tagEl.style.cssText = 'font-size:9px;color:var(--color-warning);';
          mRow.appendChild(tagEl);
        }
        container.appendChild(mRow);
      }
    }

    // Marketing
    const strategy = theme.entities.marketingStrategies.find((s) => s.id === project.marketingStrategyId);
    if (strategy) {
      const mktRow = document.createElement('div');
      mktRow.style.cssText = 'font-size:11px;color:var(--color-text-secondary);margin-top:4px;';
      mktRow.textContent = `Marketing: ${strategy.name}`;
      container.appendChild(mktRow);
    }

    // Actions
    const actionsLabel = document.createElement('div');
    actionsLabel.textContent = 'ACTIONS';
    actionsLabel.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-top:8px;';
    container.appendChild(actionsLabel);

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;';

    // Ship (only if ready)
    if (project.phase === 'ready') {
      const shipBtn = document.createElement('button');
      shipBtn.className = 'btn btn--primary';
      shipBtn.textContent = 'Ship Game';
      shipBtn.style.cssText = 'font-size:12px;padding:6px 16px;';
      shipBtn.addEventListener('click', () => {
        store.dispatch({ type: 'SHIP_PROJECT', projectId });
        dialog.close();
      });
      btnRow.appendChild(shipBtn);

      const debugBtn = document.createElement('button');
      debugBtn.className = 'btn';
      debugBtn.textContent = 'Debug More';
      debugBtn.style.cssText = 'font-size:12px;padding:6px 16px;';
      debugBtn.addEventListener('click', () => {
        store.dispatch({ type: 'DEBUG_MORE', projectId });
        statusEl.textContent = 'Back to debugging...';
        render();
      });
      btnRow.appendChild(debugBtn);
    }

    // Rename
    const renameBtn = document.createElement('button');
    renameBtn.className = 'btn';
    renameBtn.textContent = 'Rename';
    renameBtn.style.cssText = 'font-size:11px;padding:4px 10px;';
    renameBtn.addEventListener('click', () => {
      const newName = prompt('New project name:', project.name);
      if (newName && newName.trim()) {
        store.dispatch({ type: 'RENAME_PROJECT', projectId, name: newName.trim() });
        render();
      }
    });
    btnRow.appendChild(renameBtn);

    // Cancel
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn btn--danger';
    cancelBtn.textContent = 'Cancel Project';
    cancelBtn.style.cssText = 'font-size:11px;padding:4px 10px;';
    cancelBtn.addEventListener('click', () => {
      if (confirm(`Cancel "${project.name}"? All progress will be lost.`)) {
        store.dispatch({ type: 'CANCEL_PROJECT', projectId });
        dialog.close();
      }
    });
    btnRow.appendChild(cancelBtn);

    container.appendChild(btnRow);
  }

  render();
  container.appendChild(statusEl);
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
